import { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { ExpandableFilter } from './expandable-filter';

interface DistanceFilterProps {
  title: string;
  options: { value: string; label: string }[];
  selectedValue: string;
  onSelectionChange: (value: string) => void;
  userLocation: { lat: number; lng: number } | null;
  onLocationUpdate: (location: { lat: number; lng: number } | null) => void;
  facetCounts?: Record<string, number>;
}

export function DistanceFilter({
  title,
  options,
  selectedValue,
  onSelectionChange,
  userLocation,
  onLocationUpdate,
  facetCounts,
}: DistanceFilterProps) {
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [hasRequestedLocation, setHasRequestedLocation] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<string | null>(null);

  const requestLocation = () => {
    if (!navigator.geolocation) return;

    setIsLoadingLocation(true);
    setHasRequestedLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        onLocationUpdate(location);
        setIsLoadingLocation(false);
      },
      () => {
        setIsLoadingLocation(false);
        onLocationUpdate(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // Cache location for 5 minutes
      }
    );
  };

  const handleSelectionChange = (value: string) => {
    // Allow selecting "all" anytime
    if (value === 'all') {
      onSelectionChange(value);
      setPendingSelection(null);
      return;
    }

    // For distance options, check if location is available
    if (!userLocation) {
      // Store the pending selection
      setPendingSelection(value);
      // Request location if not already requested
      if (!hasRequestedLocation && !isLoadingLocation) {
        requestLocation();
      }
      // Don't change selection until location is available
      return;
    }

    // Location is available, allow selection change
    onSelectionChange(value);
  };

  // Effect to apply pending selection when location becomes available
  useEffect(() => {
    if (userLocation && pendingSelection) {
      onSelectionChange(pendingSelection);
      setPendingSelection(null);
    }
  }, [userLocation, pendingSelection, onSelectionChange]);

  const renderOption = (value: string) => {
    const option = options.find((o) => o.value === value);
    const label = option ? option.label : value;

    // Get count from facetCounts if available
    const count = facetCounts?.[value] || 0;

    // Disable non-all options if no location
    const isDisabled = !userLocation && value !== 'all';

    if (isDisabled) {
      return (
        <span className="flex items-center justify-between w-full opacity-50">
          <span>{label}</span>
        </span>
      );
    }

    return (
      <span className="flex items-center justify-between w-full">
        <span>{label}</span>
        {userLocation && facetCounts && value !== 'all' && (
          <span className="text-xs text-gray-500 ml-2">({count})</span>
        )}
      </span>
    );
  };

  // Filter options to hide those with count === 0 (but keep if count is null/undefined)
  const filteredOptions = options.filter((o) => {
    // Always show "all" option
    if (o.value === 'all') return true;
    // Always show the currently selected option
    if (o.value === selectedValue) return true;
    // If no facet counts available, show all options
    if (!facetCounts) return true;
    // Hide if count is exactly 0, show if null/undefined or > 0
    const count = facetCounts[o.value];
    return count !== 0;
  });

  return (
    <ExpandableFilter
      title={title}
      icon={<MapPin className="h-4 w-4" />}
      options={filteredOptions.map((o) => o.value)}
      selectedValue={selectedValue}
      onSelectionChange={handleSelectionChange}
      initialLimit={6}
      renderOption={renderOption}
    />
  );
}
